// useDoorState.js
// Phase 6: Door division - UI state and simple handlers
// API handlers remain in ProjectDetail.js due to external dependencies

import { useState, useCallback } from 'react';

export const useDoorState = () => {
  // ============================================
  // OPERATION LOADING STATE (prevents double-submit)
  // ============================================
  const [operationLoading, setOperationLoading] = useState(false);
  
  // ============================================
  // DOOR TYPE STATE
  // ============================================
  const [showDoorTypeDialog, setShowDoorTypeDialog] = useState(false);
  const [editingDoorType, setEditingDoorType] = useState(null);
  const [selectedDoorTypeIds, setSelectedDoorTypeIds] = useState([]);
  const [doorTypePictureUploading, setDoorTypePictureUploading] = useState(null);
  const [pendingDoorTypePicture, setPendingDoorTypePicture] = useState(null);
  
  const [newDoorType, setNewDoorType] = useState({
    name: '',
    doorType: '',
    manufacturer: '',
    model: '',
    doorMaterial: '',
    operationType: '',
    finishInterior: '',
    finishExterior: '',
    finishType: '',
    fireRated: '',
    frameWarrantyYears: '',
    finishWarrantyYears: '',
    hardwareWarrantyYears: '',
    installationWarrantyYears: '',
    markerColor: '#39FF14'
  });

  // ============================================
  // DOOR GLASS TYPE STATE
  // ============================================
  const [showDoorGlassTypeDialog, setShowDoorGlassTypeDialog] = useState(false);
  const [editingDoorGlassType, setEditingDoorGlassType] = useState(null);
  const [selectedDoorGlassTypeIds, setSelectedDoorGlassTypeIds] = useState([]);
  const [expandedDoorGlassTypeIds, setExpandedDoorGlassTypeIds] = useState([]);
  const [activeDoorSubTab, setActiveDoorSubTab] = useState('types');
  const [newDoorGlassType, setNewDoorGlassType] = useState({
    name: '',
    manufacturer: '',
    model: '',
    glassBuild: '',
    glassThickness: '',
    features: [],
    hasGrid: false,
    gridSize: '',
    warrantyYears: ''
  });
  const [showDeleteDoorGlassTypeModal, setShowDeleteDoorGlassTypeModal] = useState(false);
  const [deleteDoorGlassTypeId, setDeleteDoorGlassTypeId] = useState(null);

  // ============================================
  // DOOR METAL PANEL TYPE STATE
  // ============================================
  const [showDoorMetalPanelTypeDialog, setShowDoorMetalPanelTypeDialog] = useState(false);
  const [editingDoorMetalPanelType, setEditingDoorMetalPanelType] = useState(null);
  const [selectedDoorMetalPanelTypeIds, setSelectedDoorMetalPanelTypeIds] = useState([]);
  const [newDoorMetalPanelType, setNewDoorMetalPanelType] = useState({
    name: '',
    manufacturer: '',
    model: '',
    thickness: '',
    colorCode: ''
  });
  const [showDeleteDoorMetalPanelTypeModal, setShowDeleteDoorMetalPanelTypeModal] = useState(false);
  const [deleteDoorMetalPanelTypeId, setDeleteDoorMetalPanelTypeId] = useState(null);

  // ============================================
  // DOOR HARDWARE SET STATE
  // ============================================
  const [showDoorHardwareSetDialog, setShowDoorHardwareSetDialog] = useState(false);
  const [doorHardwareSetStep, setDoorHardwareSetStep] = useState(1);
  const [editingDoorHardwareSet, setEditingDoorHardwareSet] = useState(null);
  const [selectedDoorHardwareSetIds, setSelectedDoorHardwareSetIds] = useState([]);
  const [newDoorHardwareSet, setNewDoorHardwareSet] = useState({
    name: '',
    items: []
  });
  const [newDoorHardwareItem, setNewDoorHardwareItem] = useState({
    description: '',
    catalogNumber: '',
    finish: '',
    manufacturer: '',
    quantity: ''
  });
  const [editingDoorHardwareItemIndex, setEditingDoorHardwareItemIndex] = useState(null);
  const [showDeleteDoorHardwareSetModal, setShowDeleteDoorHardwareSetModal] = useState(false);
  const [deleteDoorHardwareSetId, setDeleteDoorHardwareSetId] = useState(null);

  // ============================================
  // DOOR SCHEDULE STATE
  // ============================================
  const [showDoorScheduleDialog, setShowDoorScheduleDialog] = useState(false);
  const [editingDoorSchedule, setEditingDoorSchedule] = useState(null);
  const [selectedDoorScheduleIds, setSelectedDoorScheduleIds] = useState([]);
  const [newDoorSchedule, setNewDoorSchedule] = useState({
    doorTypeId: '',
    hardwareSetId: '',
    label: '',
    location: '',
    doorWidth_in: '',
    doorHeight_in: '',
    roWidth_in: '',
    roHeight_in: '',
    lites: [{ liteType: 'glass', glassTypeId: '', metalPanelTypeId: '', width_in: '', height_in: '' }]
  });
  const [showDeleteDoorScheduleModal, setShowDeleteDoorScheduleModal] = useState(false);
  const [deleteDoorScheduleId, setDeleteDoorScheduleId] = useState(null);

  // Door Schedule Filter state
  const [doorScheduleSearch, setDoorScheduleSearch] = useState('');
  const [doorScheduleFilters, setDoorScheduleFilters] = useState({ type: '', location: '' });
  const [doorScheduleSortBy, setDoorScheduleSortBy] = useState('createdAt_desc');

  // ============================================
  // DOOR DETAIL DIALOG STATE
  // ============================================
  const [showDoorDetailDialog, setShowDoorDetailDialog] = useState(false);
  const [selectedDoorDetail, setSelectedDoorDetail] = useState(null);

  // ============================================
  // DOOR FLOOR PLANS (ELEVATIONS) STATE
  // ============================================
  const [selectedDoorElevation, setSelectedDoorElevation] = useState(null);
  const [selectedDoorElevationIds, setSelectedDoorElevationIds] = useState([]);
  const [doorBulkMarkerMode, setDoorBulkMarkerMode] = useState(false);
  const [doorBulkMarkerSchedule, setDoorBulkMarkerSchedule] = useState('');
  const [pendingDoorBulkMarkers, setPendingDoorBulkMarkers] = useState([]);
  const [savingDoorBulkMarkers, setSavingDoorBulkMarkers] = useState(false);

  // ============================================
  // DELETE MODAL STATE (for Door Types)
  // ============================================
  const [showDeleteDoorTypePictureModal, setShowDeleteDoorTypePictureModal] = useState(false);
  const [deleteDoorTypePictureId, setDeleteDoorTypePictureId] = useState(null);
  const [showDeleteDoorTypeModal, setShowDeleteDoorTypeModal] = useState(false);
  const [deleteDoorTypeId, setDeleteDoorTypeId] = useState(null);

  // ============================================
  // SIMPLE HANDLERS (No API calls)
  // ============================================

  // Reset new door type form
  const resetNewDoorType = useCallback(() => {
    setNewDoorType({
      name: '',
      doorType: '',
      manufacturer: '',
      model: '',
      doorMaterial: '',
      operationType: '',
      finishInterior: '',
      finishExterior: '',
      finishType: '',
      fireRated: '',
      frameWarrantyYears: '',
      finishWarrantyYears: '',
      hardwareWarrantyYears: '',
      installationWarrantyYears: '',
      markerColor: '#39FF14'
    });
    setPendingDoorTypePicture(null);
  }, []);

  // Reset new door glass type form
  const resetNewDoorGlassType = useCallback(() => {
    setNewDoorGlassType({
      name: '',
      manufacturer: '',
      glassBuild: '',
      glassThickness: '',
      features: [],
      hasGrid: false,
      gridSize: '',
      warrantyYears: ''
    });
  }, []);

  // Reset new door metal panel type form
  const resetNewDoorMetalPanelType = useCallback(() => {
    setNewDoorMetalPanelType({
      name: '',
      manufacturer: '',
      model: '',
      thickness: '',
      colorCode: ''
    });
  }, []);

  // Reset new door hardware set form
  const resetNewDoorHardwareSet = useCallback(() => {
    setNewDoorHardwareSet({ name: '', items: [] });
    setNewDoorHardwareItem({
      description: '',
      catalogNumber: '',
      finish: '',
      manufacturer: '',
      quantity: ''
    });
    setDoorHardwareSetStep(1);
    setEditingDoorHardwareItemIndex(null);
  }, []);

  // Reset new door schedule form
  const resetNewDoorSchedule = useCallback(() => {
    setNewDoorSchedule({
      doorTypeId: '',
      hardwareSetId: '',
      label: '',
      location: '',
      doorWidth_in: '',
      doorHeight_in: '',
      roWidth_in: '',
      roHeight_in: '',
      lites: [{ liteType: 'glass', glassTypeId: '', metalPanelTypeId: '', width_in: '', height_in: '' }]
    });
  }, []);

  // Clear all door selections
  const clearDoorSelections = useCallback(() => {
    setSelectedDoorTypeIds([]);
    setSelectedDoorGlassTypeIds([]);
    setSelectedDoorMetalPanelTypeIds([]);
    setSelectedDoorHardwareSetIds([]);
    setSelectedDoorScheduleIds([]);
  }, []);

  // Handle edit door type - populates form
  const handleEditDoorType = useCallback((doorType) => {
    setEditingDoorType(doorType);
    setNewDoorType({
      name: doorType.name || '',
      doorType: doorType.doorType || '',
      manufacturer: doorType.manufacturer || '',
      model: doorType.model || '',
      doorMaterial: doorType.doorMaterial || '',
      operationType: doorType.operationType || '',
      finishInterior: doorType.finishInterior || '',
      finishExterior: doorType.finishExterior || '',
      finishType: doorType.finishType || '',
      fireRated: doorType.fireRated || '',
      frameWarrantyYears: doorType.frameWarrantyYears || '',
      finishWarrantyYears: doorType.finishWarrantyYears || '',
      hardwareWarrantyYears: doorType.hardwareWarrantyYears || '',
      installationWarrantyYears: doorType.installationWarrantyYears || ''
    });
    setShowDoorTypeDialog(true);
  }, []);

  // Handle close door type dialog
  const handleCloseDoorTypeDialog = useCallback((open) => {
    setShowDoorTypeDialog(open);
    if (!open) {
      setEditingDoorType(null);
      resetNewDoorType();
    }
  }, [resetNewDoorType]);

  // Confirm delete door type - opens modal
  const confirmDeleteDoorType = useCallback((doorType, e) => {
    if (e) e.stopPropagation();
    setDeleteDoorTypeId(doorType.id);
    setShowDeleteDoorTypeModal(true);
  }, []);

  // Close delete door type modal
  const closeDeleteDoorTypeModal = useCallback(() => {
    setShowDeleteDoorTypeModal(false);
    setDeleteDoorTypeId(null);
  }, []);

  // View door detail
  const handleViewDoorDetail = useCallback((door) => {
    setSelectedDoorDetail(door);
    setShowDoorDetailDialog(true);
  }, []);

  // ============================================
  // RETURN ALL STATE AND HANDLERS
  // ============================================
  return {
    // Operation loading (prevents double-submit)
    operationLoading,
    setOperationLoading,
    
    // Door Type state
    showDoorTypeDialog, setShowDoorTypeDialog,
    editingDoorType, setEditingDoorType,
    selectedDoorTypeIds, setSelectedDoorTypeIds,
    doorTypePictureUploading, setDoorTypePictureUploading,
    pendingDoorTypePicture, setPendingDoorTypePicture,
    newDoorType, setNewDoorType,
    showDeleteDoorTypePictureModal, setShowDeleteDoorTypePictureModal,
    deleteDoorTypePictureId, setDeleteDoorTypePictureId,
    showDeleteDoorTypeModal, setShowDeleteDoorTypeModal,
    deleteDoorTypeId, setDeleteDoorTypeId,

    // Door Glass Type state
    showDoorGlassTypeDialog, setShowDoorGlassTypeDialog,
    editingDoorGlassType, setEditingDoorGlassType,
    selectedDoorGlassTypeIds, setSelectedDoorGlassTypeIds,
    expandedDoorGlassTypeIds, setExpandedDoorGlassTypeIds,
    activeDoorSubTab, setActiveDoorSubTab,
    newDoorGlassType, setNewDoorGlassType,
    showDeleteDoorGlassTypeModal, setShowDeleteDoorGlassTypeModal,
    deleteDoorGlassTypeId, setDeleteDoorGlassTypeId,

    // Door Metal Panel Type state
    showDoorMetalPanelTypeDialog, setShowDoorMetalPanelTypeDialog,
    editingDoorMetalPanelType, setEditingDoorMetalPanelType,
    selectedDoorMetalPanelTypeIds, setSelectedDoorMetalPanelTypeIds,
    newDoorMetalPanelType, setNewDoorMetalPanelType,
    showDeleteDoorMetalPanelTypeModal, setShowDeleteDoorMetalPanelTypeModal,
    deleteDoorMetalPanelTypeId, setDeleteDoorMetalPanelTypeId,

    // Door Hardware Set state
    showDoorHardwareSetDialog, setShowDoorHardwareSetDialog,
    doorHardwareSetStep, setDoorHardwareSetStep,
    editingDoorHardwareSet, setEditingDoorHardwareSet,
    selectedDoorHardwareSetIds, setSelectedDoorHardwareSetIds,
    newDoorHardwareSet, setNewDoorHardwareSet,
    newDoorHardwareItem, setNewDoorHardwareItem,
    editingDoorHardwareItemIndex, setEditingDoorHardwareItemIndex,
    showDeleteDoorHardwareSetModal, setShowDeleteDoorHardwareSetModal,
    deleteDoorHardwareSetId, setDeleteDoorHardwareSetId,

    // Door Schedule state
    showDoorScheduleDialog, setShowDoorScheduleDialog,
    editingDoorSchedule, setEditingDoorSchedule,
    selectedDoorScheduleIds, setSelectedDoorScheduleIds,
    newDoorSchedule, setNewDoorSchedule,
    showDeleteDoorScheduleModal, setShowDeleteDoorScheduleModal,
    deleteDoorScheduleId, setDeleteDoorScheduleId,

    // Door Schedule Filter state
    doorScheduleSearch, setDoorScheduleSearch,
    doorScheduleFilters, setDoorScheduleFilters,
    doorScheduleSortBy, setDoorScheduleSortBy,

    // Door Detail Dialog state
    showDoorDetailDialog, setShowDoorDetailDialog,
    selectedDoorDetail, setSelectedDoorDetail,

    // Door Floor Plans (Elevations) state
    selectedDoorElevation, setSelectedDoorElevation,
    selectedDoorElevationIds, setSelectedDoorElevationIds,
    doorBulkMarkerMode, setDoorBulkMarkerMode,
    doorBulkMarkerSchedule, setDoorBulkMarkerSchedule,
    pendingDoorBulkMarkers, setPendingDoorBulkMarkers,
    savingDoorBulkMarkers, setSavingDoorBulkMarkers,

    // Simple handlers
    resetNewDoorType,
    resetNewDoorGlassType,
    resetNewDoorMetalPanelType,
    resetNewDoorHardwareSet,
    resetNewDoorSchedule,
    clearDoorSelections,
    handleEditDoorType,
    handleCloseDoorTypeDialog,
    confirmDeleteDoorType,
    closeDeleteDoorTypeModal,
    handleViewDoorDetail
  };
};

export default useDoorState;
